/**
 * ANNOUNCE-002 — read-acknowledgement report modal (IT Manager).
 */
import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Clock } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';

export const AnnouncementReadsModal = ({ isOpen, onClose, announcementId, apiCall }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !announcementId) return;
    setLoading(true);
    setData(null);
    apiCall(`/api/it-dashboard/announcements/${announcementId}/reads`)
      .then(setData)
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [isOpen, announcementId, apiCall]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="announcement-reads-modal">
        <div className="modal-header">
          <h3>Read Acknowledgements</h3>
          <button className="modal-close" onClick={onClose} data-testid="reads-modal-close"><X size={18} /></button>
        </div>
        <div style={{ padding: '4px 20px 20px', maxHeight: '65vh', overflowY: 'auto' }}>
          {loading && <p style={{ color: '#64748b' }}>Loading…</p>}
          {data?.error && <p style={{ color: '#dc2626' }}>Failed to load read report.</p>}
          {data && !data.error && (
            <>
              <p style={{ margin: '6px 0 14px', fontSize: 14 }}>
                <strong>{data.title}</strong>
                <span style={{ color: '#64748b' }}> · {data.audience === 'all' ? 'All Staff' : 'IT Team'} · </span>
                <strong data-testid="reads-summary">{data.read_count}/{data.target_count} read</strong>
              </p>
              <h4 style={{ fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={14} /> Read ({data.read_count})
              </h4>
              {data.readers.length === 0 ? (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>No one has read it yet.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 14px' }}>
                  {data.readers.map((r) => (
                    <li key={r.user_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span>{r.user_name}</span>
                      <span style={{ color: '#64748b' }}>{formatDateTimeLocal(r.read_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <h4 style={{ fontSize: 13, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={14} /> Not yet read ({data.unread_users.length})
              </h4>
              {data.unread_users.length === 0 ? (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>Everyone has read this announcement.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {data.unread_users.map((u) => (
                    <li key={u.id} style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '3px 10px' }}>
                      {u.name}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementReadsModal;
